import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Clock } from "lucide-react";

/**
 * 24-hour intake time picker: a trigger button that opens a two-column
 * (hour / minute) scroll wheel. Value in and out is "HH:MM" ("" = no time),
 * the same shape <input type="time"> produced, so callers swap it in 1:1.
 * `hour12` shows "6:30 PM" and adds an AM/PM column; the value stays 24-hour.
 *
 * - Scroll, drag, mouse wheel (one step per notch), click an item, or use
 *   ↑/↓/Home/End on a focused column. Done / Enter save what the wheel
 *   shows; clicking outside (or the button again) saves only if the wheel
 *   was actually moved, so a peek never sets a time; Escape discards.
 * - The panel is portalled with fixed positioning so a scrolling modal body
 *   can't clip it, and it flips above the trigger when there's no room below.
 */

const ITEM_H = 40;
const VISIBLE_ROWS = 5;
const EDGE_PAD = ITEM_H * Math.floor(VISIBLE_ROWS / 2);
const WHEEL_H = ITEM_H * VISIBLE_ROWS;
const PANEL_W = 232;
const PANEL_W_12H = 280; // a third column for AM/PM
const PANEL_H = WHEEL_H + 104; // column labels + wheel + footer
const ANIM_MS = 150;

const pad2 = (n) => String(n).padStart(2, "0");
const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));
const HOURS_12 = Array.from({ length: 12 }, (_, i) => pad2(i + 1));
const PERIODS = ["AM", "PM"];
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/** "18" → { hour: "06", period: "PM" }; "00" → { hour: "12", period: "AM" }. */
const to12 = (h24) => {
  const h = Number(h24);
  return { hour: pad2(h % 12 === 0 ? 12 : h % 12), period: h < 12 ? "AM" : "PM" };
};
/** ("06", "PM") → "18"; ("12", "AM") → "00". */
const to24 = (hour, period) => pad2((Number(hour) % 12) + (period === "PM" ? 12 : 0));

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function parseHHMM(value) {
  const m = /^(\d{1,2}):(\d{2})/.exec(value || "");
  if (!m) return null;
  return { h: pad2(clamp(Number(m[1]), 0, 23)), m: pad2(clamp(Number(m[2]), 0, 59)) };
}

function WheelColumn({ values, selected, onSelect, label, columnRef }) {
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const frame = useRef(0);
  const settleTimer = useRef(0);
  const targetIndex = useRef(0);
  const wheelAccum = useRef(0);
  const onSelectRef = useRef(onSelect);
  const selectedRef = useRef(selected);
  onSelectRef.current = onSelect;
  selectedRef.current = selected;
  const reduced = useMemo(prefersReducedMotion, []);

  const setRefs = (el) => {
    scrollRef.current = el;
    if (columnRef) columnRef.current = el;
  };

  // Land on the current value instantly when the panel opens.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.max(0, values.indexOf(selectedRef.current));
    targetIndex.current = idx;
    el.scrollTop = idx * ITEM_H;
    setScrollTop(el.scrollTop);
    // Only on mount — later selection changes come from this column itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToIndex = useCallback(
    (i) => {
      const el = scrollRef.current;
      if (!el) return;
      const idx = clamp(i, 0, values.length - 1);
      targetIndex.current = idx;
      el.scrollTo({ top: idx * ITEM_H, behavior: reduced ? "auto" : "smooth" });
    },
    [values.length, reduced]
  );

  const handleScroll = () => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (!el) return;
      setScrollTop(el.scrollTop);
      // Live selection: whatever sits in the centre band is the value.
      const idx = clamp(Math.round(el.scrollTop / ITEM_H), 0, values.length - 1);
      if (values[idx] !== selectedRef.current) onSelectRef.current(values[idx]);
    });
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      const el = scrollRef.current;
      if (el) targetIndex.current = clamp(Math.round(el.scrollTop / ITEM_H), 0, values.length - 1);
    }, 120);
  };

  // A mouse-wheel notch is ~100px, which would skip 2–3 rows under snap.
  // Step exactly one row per notch instead; trackpads accumulate to a row.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      wheelAccum.current += e.deltaY;
      if (Math.abs(wheelAccum.current) < ITEM_H) return;
      const step = Math.sign(wheelAccum.current);
      wheelAccum.current = 0;
      scrollToIndex(targetIndex.current + step);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [scrollToIndex]);

  useEffect(
    () => () => {
      cancelAnimationFrame(frame.current);
      clearTimeout(settleTimer.current);
    },
    []
  );

  const handleKeyDown = (e) => {
    const moves = { ArrowDown: 1, ArrowUp: -1, PageDown: 5, PageUp: -5 };
    if (moves[e.key] != null) {
      e.preventDefault();
      scrollToIndex(targetIndex.current + moves[e.key]);
    } else if (e.key === "Home") {
      e.preventDefault();
      scrollToIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      scrollToIndex(values.length - 1);
    }
  };

  const fade = "linear-gradient(to bottom, transparent 0%, #000 32%, #000 68%, transparent 100%)";

  return (
    <div
      ref={setRefs}
      role="listbox"
      aria-label={label}
      tabIndex={0}
      onScroll={handleScroll}
      onKeyDown={handleKeyDown}
      className="relative z-10 flex-1 overflow-y-auto overscroll-contain snap-y snap-mandatory rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300"
      style={{
        height: WHEEL_H,
        scrollbarWidth: "none",
        perspective: "500px",
        maskImage: fade,
        WebkitMaskImage: fade,
      }}
    >
      <div style={{ height: EDGE_PAD }} aria-hidden="true" />
      {values.map((v, i) => {
        const d = (i * ITEM_H - scrollTop) / ITEM_H; // rows from centre
        const ad = Math.min(Math.abs(d), 3);
        const isSelected = v === selected;
        return (
          <div
            key={v}
            role="option"
            aria-selected={isSelected}
            onClick={() => scrollToIndex(i)}
            className={`snap-center flex items-center justify-center cursor-pointer select-none tabular-nums transition-colors duration-150 ${
              isSelected ? "text-lg font-semibold text-orange-700" : "text-base text-gray-500 hover:text-gray-800"
            }`}
            style={{
              height: ITEM_H,
              opacity: 1 - ad * 0.25,
              transform: reduced
                ? undefined
                : `rotateX(${clamp(-d * 18, -60, 60)}deg) scale(${1 - ad * 0.05})`,
            }}
          >
            {v}
          </div>
        );
      })}
      <div style={{ height: EDGE_PAD }} aria-hidden="true" />
    </div>
  );
}

export default function IntakeTimePicker({
  value = "",
  onChange,
  disabled = false,
  placeholder = "Set time",
  // Where the wheel starts when there is no value yet.
  defaultTime = "08:00",
  minuteStep = 5,
  className = "",
  id,
  // The open panel's accessible name.
  panelLabel = "Choose intake time",
  // Show and pick the time as "6:30 PM"; the value stays 24-hour "HH:MM".
  hour12 = false,
  // Show a Clear button in the panel; turn off when the caller owns clearing.
  clearable = true,
}) {
  const panelW = hour12 ? PANEL_W_12H : PANEL_W;
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [draft, setDraft] = useState({ h: "08", m: "00" });
  // Minute captured at open: an off-step saved minute (e.g. 07:32) stays in
  // the list for the whole session so the column never reflows mid-scroll.
  const [openMinute, setOpenMinute] = useState("00");
  const [pos, setPos] = useState({ top: 0, left: 0, above: false });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const hourColRef = useRef(null);
  const closeTimer = useRef(0);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // True once the user moves either column during this open.
  const touchedRef = useRef(false);

  const parsed = parseHHMM(value);
  const display = (() => {
    if (!parsed) return "";
    if (!hour12) return `${parsed.h}:${parsed.m}`;
    const { hour, period } = to12(parsed.h);
    return `${Number(hour)}:${parsed.m} ${period}`;
  })();
  const draft12 = to12(draft.h);

  const minutes = useMemo(() => {
    const list = [];
    for (let i = 0; i < 60; i += minuteStep) list.push(pad2(i));
    if (!list.includes(openMinute)) list.push(openMinute);
    return list.sort();
  }, [minuteStep, openMinute]);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const above = spaceBelow < PANEL_H + 12 && r.top > spaceBelow;
    setPos({
      top: above ? Math.max(8, r.top - PANEL_H - 6) : r.bottom + 6,
      left: clamp(r.left, 8, window.innerWidth - panelW - 8),
      above,
    });
  }, [panelW]);

  const openPicker = () => {
    if (disabled) return;
    clearTimeout(closeTimer.current);
    const start = parsed || parseHHMM(defaultTime) || { h: "08", m: "00" };
    setDraft(start);
    setOpenMinute(start.m);
    touchedRef.current = false;
    place();
    setOpen(true);
  };

  // mode: "done" (explicit save), "dismiss" (save only if the wheel moved),
  // or false (discard).
  const closePicker = useCallback(
    (mode) => {
      const commit = mode === "done" || (mode === "dismiss" && touchedRef.current);
      if (commit) {
        const { h, m } = draftRef.current;
        onChange?.(`${h}:${m}`);
      }
      setVisible(false);
      clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(
        () => setOpen(false),
        prefersReducedMotion() ? 0 : ANIM_MS
      );
      triggerRef.current?.focus({ preventScroll: true });
    },
    [onChange]
  );

  // Enter animation + focus the hour column once mounted.
  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(() => {
      setVisible(true);
      hourColRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Keep the panel pinned to the trigger while the page/modal scrolls.
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  // Clicking outside keeps the choice (like a native wheel picker).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      closePicker("dismiss");
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open, closePicker]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handlePanelKeyDown = (e) => {
    // Stop here so the surrounding modal's own Escape/Enter handlers don't
    // also close it or submit.
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closePicker(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      closePicker("done");
    }
  };

  const panel = open
    ? createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label={panelLabel}
          onKeyDown={handlePanelKeyDown}
          onClick={(e) => e.stopPropagation()}
          className="fixed z-[70] rounded-2xl border border-gray-200 bg-white p-3 shadow-xl transition-[opacity,transform] ease-out"
          style={{
            top: pos.top,
            left: pos.left,
            width: panelW,
            transitionDuration: `${ANIM_MS}ms`,
            opacity: visible ? 1 : 0,
            transform: visible
              ? "translateY(0) scale(1)"
              : `translateY(${pos.above ? 6 : -6}px) scale(0.97)`,
            transformOrigin: pos.above ? "bottom left" : "top left",
          }}
        >
          <div className="mb-1 flex text-[11px] font-medium uppercase tracking-wide text-gray-400">
            <span className="flex-1 text-center">Hour</span>
            <span className="w-4" />
            <span className="flex-1 text-center">Min</span>
            {hour12 ? <span className="flex-1 text-center" /> : null}
          </div>

          <div className="relative flex items-center">
            {/* Selection band spanning both columns */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 rounded-lg bg-orange-50 ring-1 ring-orange-200"
              style={{ top: EDGE_PAD, height: ITEM_H }}
            />
            {hour12 ? (
              <WheelColumn
                values={HOURS_12}
                selected={draft12.hour}
                onSelect={(hour) => {
                  touchedRef.current = true;
                  setDraft((d) => ({ ...d, h: to24(hour, to12(d.h).period) }));
                }}
                label="Hour"
                columnRef={hourColRef}
              />
            ) : (
              <WheelColumn
                values={HOURS}
                selected={draft.h}
                onSelect={(h) => {
                  touchedRef.current = true;
                  setDraft((d) => ({ ...d, h }));
                }}
                label="Hour"
                columnRef={hourColRef}
              />
            )}
            <span className="relative z-10 w-4 text-center text-lg font-semibold text-orange-700">:</span>
            <WheelColumn
              values={minutes}
              selected={draft.m}
              onSelect={(m) => {
                touchedRef.current = true;
                setDraft((d) => ({ ...d, m }));
              }}
              label="Minute"
            />
            {hour12 ? (
              <WheelColumn
                values={PERIODS}
                selected={draft12.period}
                onSelect={(period) => {
                  touchedRef.current = true;
                  setDraft((d) => ({ ...d, h: to24(to12(d.h).hour, period) }));
                }}
                label="AM or PM"
              />
            ) : null}
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            {clearable && display ? (
              <button
                type="button"
                onClick={() => {
                  onChange?.("");
                  closePicker(false);
                }}
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
              >
                Clear
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={() => closePicker("done")}
              className="rounded-lg bg-orange-primary px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 active:scale-[0.98]"
            >
              Done
            </button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => (open ? closePicker("dismiss") : openPicker())}
        className={`inline-flex min-w-[9rem] items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-orange-primary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70 ${
          open ? "border-orange-primary bg-orange-50/40" : "border-gray-300 bg-white hover:border-orange-primary"
        } ${className}`}
      >
        <Clock className={`h-4 w-4 ${display ? "text-orange-primary" : "text-gray-400"}`} />
        <span className={`flex-1 text-left tabular-nums ${display ? "font-medium text-gray-900" : "text-gray-400"}`}>
          {display || placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {panel}
    </>
  );
}
